import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Boxes, ArrowLeftRight, History } from 'lucide-react';
import { erpList, erpStockTxn, erpStockLedger } from '../../../services/erpApi';
import { Card, Button, Input, Select, PageHeader, Segmented, Table, FormModal, EmptyState, Badge, SearchBox, Drawer, fmtNum, fmtDate } from '../ui';

const TXN_TYPES = [
  ['purchase_receipt', 'Purchase receipt (+)'], ['production_receipt', 'Production receipt (+)'], ['jobwork_return', 'Job work return (+)'],
  ['trial_return', 'Trial return (+)'], ['sales_delivery', 'Sales delivery (−)'], ['adjustment', 'Adjustment (±)'],
];
const EMPTY = { sku_id: '', type: 'purchase_receipt', qty: '', ref: '' };

const Qty = ({ v }) => <span className={`font-bold ${v > 0 ? 'text-emerald-600' : v < 0 ? 'text-red-600' : ''}`}>{v > 0 ? `+${fmtNum(v)}` : fmtNum(v)}</span>;
const skuCell = (r) => <div><p className="font-semibold">{r.sku_name}</p><p className="font-mono text-[11px]" style={{ color: 'var(--erp-muted)' }}>{r.sku_code}</p></div>;
const StockBadge = ({ r }) => (r.available > 0 ? <Badge tone="green">In stock</Badge> : r.physical > 0 ? <Badge tone="amber">Reserved</Badge> : <Badge tone="red">Out of stock</Badge>);

export const Inventory = ({ sub = 'position' }) => {
  const [tab, setTab] = useState(sub);
  const [rows, setRows] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [drawer, setDrawer] = useState(null);
  const [drawerRows, setDrawerRows] = useState([]);

  useEffect(() => { setTab(sub); }, [sub]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([erpList('stock', q ? { q } : undefined), erpStockLedger()]);
      setRows(s.data.items || []); setLedger(l.data.items || []);
    } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const post = async () => {
    setBusy(true);
    try { await erpStockTxn({ ...form, qty: parseInt(form.qty, 10) }); toast.success('Movement posted'); setModal(false); setForm(EMPTY); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };
  const openLedger = async (r) => { setDrawer(r); const l = await erpStockLedger(r.sku_id); setDrawerRows(l.data.items || []); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const positionCols = [
    { key: 'sku', label: 'SKU', render: skuCell },
    { key: 'physical', label: 'Physical', align: 'right', render: (r) => <span className="font-bold">{fmtNum(r.physical)}</span> },
    { key: 'reserved', label: 'Reserved', align: 'right', render: (r) => fmtNum(r.reserved) },
    { key: 'available', label: 'Available', align: 'right', render: (r) => <span className="font-extrabold" style={{ color: r.available > 0 ? '#127a45' : '#c0262d' }}>{fmtNum(r.available)}</span> },
    { key: 'status', label: 'Status', render: (r) => <StockBadge r={r} /> },
    { key: 'updated_at', label: 'Updated', render: (r) => <span style={{ color: 'var(--erp-muted)' }}>{fmtDate(r.updated_at)}</span> },
  ];
  const ledgerCols = [
    { key: 'at', label: 'Date', render: (t) => fmtDate(t.at) },
    { key: 'sku', label: 'SKU', render: skuCell },
    { key: 'type', label: 'Type', render: (t) => <Badge tone="blue">{t.type.replace(/_/g, ' ')}</Badge> },
    { key: 'qty', label: 'Qty', align: 'right', render: (t) => <Qty v={t.qty} /> },
    { key: 'balance_after', label: 'Balance', align: 'right', render: (t) => <span className="font-semibold">{fmtNum(t.balance_after)}</span> },
    { key: 'ref', label: 'Reference', render: (t) => t.ref || '—' },
  ];

  return (
    <div data-testid="erp-inventory">
      <PageHeader crumb="Inventory" title={tab === 'ledger' ? 'Stock Ledger' : 'Stock Position'} subtitle="Transaction-led stock: physical · reserved · available. Every movement is auditable."
        actions={<Button icon={Plus} onClick={() => setModal(true)} data-testid="txn-open">Post Movement</Button>} />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <Segmented testid="inventory-tab" value={tab} onChange={setTab} options={[{ value: 'position', label: 'Stock Position' }, { value: 'ledger', label: 'Stock Ledger' }]} />
        {tab === 'position' && <SearchBox value={q} onChange={setQ} placeholder="Search SKU code or name…" className="w-72" data-testid="stock-search" />}
      </div>
      <Card className="p-0 erp-rise erp-d1">
        {tab === 'position' ? (
          <Table testid="stock-table" rowKey="sku_id" columns={positionCols} rows={rows} loading={loading} onRowClick={openLedger}
            empty={<EmptyState icon={Boxes} title="No stock records" text="Create SKUs under Products, then post a purchase receipt." />} />
        ) : (
          <Table testid="ledger-table" columns={ledgerCols} rows={ledger} loading={loading}
            empty={<EmptyState icon={History} title="No movements yet" text="Every posted movement will appear here in order." action={<Button icon={Plus} onClick={() => setModal(true)}>Post Movement</Button>} />} />
        )}
      </Card>

      <FormModal open={modal} onClose={() => setModal(false)} onSubmit={post} busy={busy} testid="txn-modal" submitLabel="Post" title="Post stock movement" subtitle="Updates physical stock and writes an immutable ledger entry.">
        <Select label="SKU" required value={form.sku_id} onChange={set('sku_id')} data-testid="txn-sku">
          <option value="">Select SKU</option>
          {rows.map((r) => <option key={r.sku_id} value={r.sku_id}>{r.sku_code} — {r.sku_name} (avail {r.available})</option>)}
        </Select>
        <div className="grid sm:grid-cols-2 gap-4">
          <Select label="Movement type" value={form.type} onChange={set('type')} data-testid="txn-type">
            {TXN_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
          <Input label="Quantity" type="number" required value={form.qty} onChange={set('qty')} placeholder="e.g. 100 or -20" hint="Use negative for outward / adjustment down" data-testid="txn-qty" />
        </div>
        <Input label="Reference" value={form.ref} onChange={set('ref')} placeholder="GRN-2607-0045 / SO-2607-0018" data-testid="txn-ref" />
      </FormModal>

      <Drawer open={!!drawer} onClose={() => setDrawer(null)} title={drawer?.sku_name} subtitle={drawer?.sku_code} testid="ledger-drawer">
        {drawer && (
          <>
            <div className="grid grid-cols-3 gap-3 mb-5">
              {[['Physical', drawer.physical], ['Reserved', drawer.reserved], ['Available', drawer.available]].map(([l, v]) => (
                <div key={l} className="rounded-xl p-3" style={{ background: '#f5f7fb' }}><p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--erp-muted)' }}>{l}</p><p className="text-xl font-extrabold mt-0.5">{fmtNum(v)}</p></div>
              ))}
            </div>
            <div className="flex items-center gap-2 mb-2 text-sm font-bold"><ArrowLeftRight size={15} /> Movement history</div>
            {drawerRows.length === 0 ? <p className="text-sm" style={{ color: 'var(--erp-muted)' }}>No movements for this SKU.</p> : (
              <ul className="divide-y" style={{ borderColor: 'var(--erp-line)' }}>
                {drawerRows.map((t) => (
                  <li key={t.id} className="py-2.5 flex items-center gap-3 text-sm">
                    <div className="flex-1 min-w-0"><p className="font-semibold capitalize">{t.type.replace(/_/g, ' ')}</p><p className="text-xs truncate" style={{ color: 'var(--erp-muted)' }}>{t.ref || 'No reference'} · {fmtDate(t.at)}</p></div>
                    <Qty v={t.qty} /><span className="w-16 text-right text-xs" style={{ color: 'var(--erp-muted)' }}>= {fmtNum(t.balance_after)}</span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
};
